/**
 * TransformSystem: world matrices for every entity, cached and invalidated
 * by world events (architecture §3.4).
 *
 * `Transform` stores position (m), Euler rotation (degrees, XYZ) and scale.
 * World matrix = parent world × local. A change dirties the entity and all its
 * descendants; `flush()` reports each dirty id once so the render bridge can
 * push exactly the transforms that moved.
 */
import { eulerDegToQuat } from "../core/math/euler";
import { mat4Compose, mat4Identity, mat4Multiply } from "../core/math/mat4";
import type { Mat4 } from "../core/math/mat4";
import type { Vec3 } from "../core/math/vec3";
import type { Unsubscribe } from "../core/Emitter";
import type { EntityId } from "./entity";
import type { WorldReader } from "./World";

interface TransformData {
  readonly position: Vec3;
  readonly rotation: Vec3;
  readonly scale: Vec3;
}

export class TransformSystem {
  private readonly cache = new Map<EntityId, Mat4>();
  private readonly dirty = new Set<EntityId>();
  private readonly unsubscribe: Unsubscribe[];

  constructor(private readonly world: WorldReader) {
    this.markAll();
    this.unsubscribe = [
      world.events.on("component:changed", (e) => {
        if (e.type === "Transform") this.markSubtree(e.id);
      }),
      world.events.on("world:replaced", () => {
        this.markAll();
      }),
      // A new parent (a move) or none (added, removed) changes the world matrix.
      world.events.on("entity:changed", (e) => {
        if (e.previous?.parent !== e.next?.parent) this.markSubtree(e.id);
      }),
    ];
  }

  /** The entity's world matrix (column-major). Entities without a Transform are identity. */
  worldMatrix(id: EntityId): Mat4 {
    const cached = this.cache.get(id);
    if (cached) return cached;

    const matrix = composeWorld(this.world, id, (parent) => this.worldMatrix(parent));
    this.cache.set(id, matrix);
    return matrix;
  }

  /** Returns (and clears) every entity whose world matrix changed since the last flush. */
  flush(): EntityId[] {
    const changed = [...this.dirty];
    this.dirty.clear();
    return changed;
  }

  dispose(): void {
    this.unsubscribe.forEach((off) => {
      off();
    });
  }

  private markSubtree(id: EntityId): void {
    this.cache.delete(id);
    this.dirty.add(id);
    for (const child of this.world.get(id)?.children ?? []) this.markSubtree(child);
  }

  private markAll(): void {
    this.cache.clear();
    for (const id of this.world.getSnapshot().entities.keys()) this.dirty.add(id);
  }
}

/** Local matrix from Transform data (degrees in, radians only inside the quaternion math). */
export function localMatrix(transform: TransformData | undefined): Mat4 {
  if (!transform) return mat4Identity();
  return mat4Compose(transform.position, eulerDegToQuat(transform.rotation), transform.scale);
}

/**
 * An entity's world matrix computed from the records, without a cache: for
 * commands (entity.move keeps world transforms), which hold only a reader.
 */
export function worldMatrixOf(world: Pick<WorldReader, "get">, id: EntityId): Mat4 {
  return composeWorld(world, id, (parent) => worldMatrixOf(world, parent));
}

/** Parent world × local; identity for unknown entities and those without a Transform. */
function composeWorld(
  world: Pick<WorldReader, "get">,
  id: EntityId,
  parentWorld: (parent: EntityId) => Mat4,
): Mat4 {
  const record = world.get(id);
  if (!record) return mat4Identity();
  const local = localMatrix(record.components.get("Transform") as TransformData | undefined);
  return record.parent === null ? local : mat4Multiply(parentWorld(record.parent), local);
}
